<!-- 試用版申込フォーム STEP1：お客様情報・管理者情報・代理店情報・送付先情報を入力する画面 -->
<template>
  <v-container fluid class="pa-0">
    <PageHeader />

    <div class="content-container">
      <StepNavigator :current="1" />

      <v-form ref="customerFormRef" @submit.prevent>
        <!-- ===============================
     ご利用になるお客様情報
     =============================== -->
        <section class="form-section">
          <h2>ご利用になるお客様情報</h2>

          <CompanyInfoForm
            v-model="form"
            :rules="rules"
            :prefectures="prefectures"
            @search-address="searchCustomerAddress"
          />
        </section>

        <!-- =======================================
         管理者情報
    ======================================== -->
        <section class="form-section">
          <h2>管理者情報</h2>

          <p class="info-desc">
            管理者様が、上記のご利用になるお客様情報と異なる場合にご入力ください。<br />
            「運用者等」は、管理者様以外にご担当される方がいる場合にご記入ください。「アカウント情報の送付先」で「管理者様」を選択された場合、初期パスワードを含むアカウント情報のメールは、この方にもお送りします。
          </p>

          <v-row style="border-bottom: 1px solid #ddd" no-gutters>
            <v-col cols="12" md="3" class="form-label"> 追記の有無 </v-col>
            <v-col cols="12" md="9" class="pl-md-12">
              <v-checkbox
                v-model="managerEnabled"
                label="管理者情報を入力する"
                hide-details
                density="compact"
              />
            </v-col>
          </v-row>

          <CompanyInfoForm
            v-if="managerEnabled"
            v-model="form.manager"
            company-label="法人名"
            :show-sub-fields="true"
            :rules="rules"
            :prefectures="prefectures"
            @search-address="searchManagerAddress"
          />
        </section>

        <!-- =======================================
         販売代理店情報
    ======================================== -->
        <section class="form-section">
          <h2>販売代理店情報</h2>

          <p class="info-desc">
            販売代理店の方がお申込みの場合は、販売代理店の情報をご記入ください。
          </p>

          <v-row style="border-bottom: 1px solid #ddd" no-gutters>
            <v-col cols="12" md="3" class="form-label"> 追記の有無 </v-col>
            <v-col cols="12" md="9" class="pl-md-12">
              <v-checkbox
                v-model="agencyEnabled"
                label="販売代理店情報を入力する"
                hide-details
                density="compact"
              />
            </v-col>
          </v-row>

          <CompanyInfoForm
            v-if="agencyEnabled"
            v-model="form.agency"
            company-label="法人名"
            :show-mail-delivery="true"
            :require-address="false"
            :require-department="false"
            :rules="rules"
            :prefectures="prefectures"
            @search-address="searchAgencyAddress"
          />
        </section>

        <!-- =======================================
         アカウント情報の送付先
    ======================================== -->
        <section class="form-section">
          <h2>アカウント情報の送付先</h2>

          <p class="info-desc">
            アカウント情報の送付先をご選択いただけます。複数選択された場合は、選択されたすべての送付先にお送りします。「その他」を選択された場合は、送付先の情報をご記入ください。
          </p>

          <v-row class="form-row" no-gutters align="center">
            <v-col cols="12" md="3" class="form-label">
              送付先
              <span class="req">必須</span>
            </v-col>
            <v-col
              cols="12"
              md="9"
              class="form-input pl-md-10 d-flex align-center"
            >
              <!-- 検証はv-input側で行い、エラーメッセージをチェックボックスの外に出す -->
              <v-input
                :model-value="form.delivery.types"
                :rules="[rules.requiredDelivery]"
                hide-details="auto"
                class="delivery-input"
              >
                <div class="delivery-checkboxes">
                  <v-checkbox
                    v-model="form.delivery.types"
                    label="ご利用になるお客様"
                    value="customer"
                    density="compact"
                    hide-details
                    class="mt-0"
                  />
                  <v-checkbox
                    v-model="form.delivery.types"
                    label="管理者様"
                    value="manager"
                    density="compact"
                    hide-details
                    class="mt-0"
                  />
                  <v-checkbox
                    v-model="form.delivery.types"
                    label="販売代理店"
                    value="agency"
                    density="compact"
                    hide-details
                    class="mt-0"
                  />
                  <v-checkbox
                    v-model="form.delivery.types"
                    label="その他"
                    value="other"
                    density="compact"
                    hide-details
                    class="mt-0"
                  />
                </div>
              </v-input>
            </v-col>
          </v-row>

          <div
            v-if="form.delivery.types.includes('other')"
            class="sub-form-block delivery-other-block"
          >
            <p class="sub-form-title">その他の送付先</p>

            <v-row class="form-row" no-gutters>
              <v-col cols="12" md="3" class="form-label">
                お名前
                <span class="req">必須</span>
              </v-col>
              <v-col cols="12" md="9" class="pl-md-12">
                <v-text-field
                  v-model="form.delivery.name"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>

            <v-row class="form-row" no-gutters>
              <v-col cols="12" md="3" class="form-label">
                メールアドレス
                <span class="req">必須</span>
              </v-col>
              <v-col cols="12" md="9" class="pl-md-12">
                <v-text-field
                  v-model="form.delivery.email"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :rules="[rules.requiredEmail]"
                />
              </v-col>
            </v-row>
          </div>
        </section>
      </v-form>

      <!-- ボタン -->
      <div class="button-area">
        <v-btn class="button-primary" @click="goNext" size="large">
          次へ
        </v-btn>
      </div>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import { useExperienceFormStore } from "../store/useExperienceFormStore.ts";
import { markStepCompleted } from "../router/index.ts";
import StepNavigator from "../components/StepNavigator.vue";
import PageHeader from "../components/layout/PageHeader.vue";
import CompanyInfoForm from "../components/form/CompanyInfoForm.vue";
import { computed, ref, nextTick } from "vue";

const customerFormRef = ref<any>(null);

const router = useRouter();

// 住所自動入力（ご利用になるお客様）
const searchCustomerAddress = async () => {
  await store.searchAddress("customer");
};

// 住所自動入力（管理者）
const searchManagerAddress = async () => {
  await store.searchAddress("manager");
};

// 住所自動入力（販売代理店）
const searchAgencyAddress = async () => {
  await store.searchAddress("agency");
};

// 都道府県
const prefectures = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
];

const managerEnabled = computed({
  get: () => store.form.managerEnabled,
  set: (v) => (store.form.managerEnabled = v),
});

const agencyEnabled = computed({
  get: () => store.form.agencyEnabled,
  set: (v) => (store.form.agencyEnabled = v),
});

const store = useExperienceFormStore();
const form = computed({
  get: () => store.form,
  set: (value) => {
    Object.assign(store.form, value);
  },
});

/* =========================
   Vuetify Rules
========================= */
const rules = {
  required: (v: any) => !!v || "必須項目です。",
  requiredDelivery: (v: any) =>
    (Array.isArray(v) && v.length > 0) || "1つ以上選択してください。",
  zip: (v: string) => {
    if (!v) return true;
    return /^\d{7}$/.test(v) || "郵便番号は半角数字7桁で入力してください。";
  },
  requiredZip: (v: string) => {
    if (!v) return "必須項目です。";
    return /^\d{7}$/.test(v) || "郵便番号は半角数字7桁で入力してください。";
  },
  email: (v: string) => {
    if (!v) return true;
    return (
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "メール形式が正しくありません。"
    );
  },
  tel: (v: string) => {
    if (!v) return true;
    return (
      /^0\d{1,4}-?\d{1,4}-?\d{3,4}$/.test(v) ||
      "電話番号の形式が正しくありません。"
    );
  },
  requiredEmail: (v: string) => {
    if (!v) return "必須項目です。";
    return (
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "メール形式が正しくありません。"
    );
  },
  requiredTel: (v: string) => {
    if (!v) return "必須項目です。";
    return (
      /^0\d{1,4}-?\d{1,4}-?\d{3,4}$/.test(v) ||
      "電話番号の形式が正しくありません。"
    );
  },
  requiredKatakana: (v: string) => {
    if (!v) return "必須項目です。";
    return /^[ァ-ヶー\s]+$/.test(v) || "カタカナで入力してください。";
  },
};

async function goNext() {
  // Vuetifyのバリデーションを実行
  const { valid } = await customerFormRef.value.validate();

  if (!valid) {
    // バリデーションエラーがある場合はスクロールして表示
    await nextTick();
    const firstError = document.querySelector(".v-input--error");
    if (firstError) {
      firstError.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
    return;
  }

  // 追加の条件チェック
  const f = form.value;

  if (f.delivery.types.includes("manager") && !store.form.managerEnabled) {
    alert(
      "「送付先」で「管理者様」が選択されています。管理者情報の入力が必要です。",
    );
    return;
  }

  if (f.delivery.types.includes("agency") && !store.form.agencyEnabled) {
    alert(
      "「送付先」で「販売代理店」が選択されています。販売代理店情報の入力が必要です。",
    );
    return;
  }

  // ステップ1完了をマーク
  markStepCompleted("step1");
  router.push("/step2");
}
</script>

<style lang="scss" global>
@use "@/assets/styles/experience-form.scss" as *;
</style>
